import React from 'react';
import { motion } from "framer-motion";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Terminal, Key, FileJson, Server, Activity, Copy } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

const endpoints = [
  {
    method: "POST",
    path: "/v1/charges",
    description: "Create a new charge or authorize a payment.",
    color: "text-emerald-400",
    bg: "bg-emerald-400/10",
    border: "border-emerald-400/20"
  },
  {
    method: "GET",
    path: "/v1/charges/:id",
    description: "Retrieve the details of a specific charge.",
    color: "text-blue-400",
    bg: "bg-blue-400/10",
    border: "border-blue-400/20"
  },
  {
    method: "POST",
    path: "/v1/refunds",
    description: "Refund a charge that has previously been created.",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    border: "border-amber-400/20"
  },
  {
    method: "GET",
    path: "/v1/balance",
    description: "Retrieve your current account balance.",
    color: "text-blue-400",
    bg: "bg-blue-400/10",
    border: "border-blue-400/20"
  }
];

const APIDocumentation = () => {
  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <Terminal className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">REST API v1.0</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Developer <span className="gradient-text">Documentation</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Build robust integrations with Luxkgate using our comprehensive RESTful API. Designed to be predictable, resource-oriented, and developer-friendly.
          </p>
        </motion.div>

        {/* Quick Start & Auth */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-slate-900/50 border-slate-800/50 h-full hover:border-slate-700 transition-colors">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                    <Server className="h-6 w-6 text-blue-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Base URL</h2>
                </div>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  All API requests should be made to our secure REST endpoint. We support only HTTPS connections to ensure data security.
                </p>
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800 flex items-center justify-between group">
                  <code className="text-sm text-emerald-400 font-mono">https://api.luxkgate.com/v1</code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="bg-slate-900/50 border-slate-800/50 h-full hover:border-slate-700 transition-colors">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                    <Key className="h-6 w-6 text-violet-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">Authentication</h2>
                </div>
                <p className="text-slate-400 mb-6 leading-relaxed">
                  Authenticate your API requests using HTTP Bearer authentication. You can generate API keys within your dashboard.
                </p>
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800 flex items-center justify-between group">
                  <code className="text-sm text-slate-300 font-mono">
                    <span className="text-violet-400">Authorization:</span> Bearer sk_live_...
                  </code>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Endpoints & Code Example */}
        <div className="grid xl:grid-cols-3 gap-8">
          <motion.div 
            className="xl:col-span-1 space-y-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-400" />
              Core Endpoints
            </h3>
            {endpoints.map((ep, i) => (
              <div key={i} className="p-4 rounded-xl bg-slate-900/30 border border-slate-800/50 hover:bg-slate-800/50 transition-colors cursor-pointer group">
                <div className="flex items-center gap-3 mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold w-12 text-center ${ep.bg} ${ep.color} ${ep.border} border`}>
                    {ep.method}
                  </span>
                  <code className="text-sm font-mono text-slate-300 group-hover:text-white transition-colors">{ep.path}</code>
                </div>
                <p className="text-xs text-slate-500">{ep.description}</p>
              </div>
            ))}
            <Button className="w-full mt-4 bg-slate-800 hover:bg-slate-700 text-white">
              View Full Reference
            </Button>
          </motion.div>

          <motion.div 
            className="xl:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card className="bg-slate-950 border-slate-800 h-full relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-12 bg-slate-900 border-b border-slate-800 flex items-center px-4 gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                </div>
                <div className="mx-auto flex items-center gap-2 text-xs text-slate-500 font-mono">
                  <FileJson className="h-3.5 w-3.5" />
                  create_charge.sh
                </div>
              </div>
              <CardContent className="p-6 pt-20">
                <pre className="text-sm font-mono text-slate-300 overflow-x-auto">
                  <code>
<span className="text-emerald-400">curl</span> https://api.luxkgate.com/v1/charges \{"\n"}
  <span className="text-blue-400">-H</span> <span className="text-amber-300">"Authorization: Bearer sk_test_4eC39HqLyjWDarjtT1zdp7dc"</span> \{"\n"}
  <span className="text-blue-400">-H</span> <span className="text-amber-300">"Content-Type: application/json"</span> \{"\n"}
  <span className="text-blue-400">-d</span> <span className="text-amber-300">'{ "{" }</span>{"\n"}
    <span className="text-violet-400">"amount"</span>: <span className="text-amber-400">2000</span>,{"\n"}
    <span className="text-violet-400">"currency"</span>: <span className="text-amber-300">"usd"</span>,{"\n"}
    <span className="text-violet-400">"source"</span>: <span className="text-amber-300">"tok_visa"</span>,{"\n"}
    <span className="text-violet-400">"description"</span>: <span className="text-amber-300">"Charge for product XYZ"</span>{"\n"}
  <span className="text-amber-300">{ "}" }'</span>
                  </code>
                </pre>
                
                <div className="mt-8 border-t border-slate-800 pt-6">
                  <div className="text-xs text-slate-500 font-mono mb-4 uppercase tracking-wider">Response</div>
                  <pre className="text-sm font-mono text-slate-400 overflow-x-auto">
                    <code>
{"{"} {"\n"}
  <span className="text-violet-400">"id"</span>: <span className="text-amber-300">"ch_1Hxyz2eZx"</span>,{"\n"}
  <span className="text-violet-400">"object"</span>: <span className="text-amber-300">"charge"</span>,{"\n"}
  <span className="text-violet-400">"amount"</span>: <span className="text-amber-400">2000</span>,{"\n"}
  <span className="text-violet-400">"status"</span>: <span className="text-amber-300">"succeeded"</span>{"\n"}
{"}"}
                    </code>
                  </pre>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default APIDocumentation;